import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Gem, History, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCredits } from "@/hooks/useCredits";
import {
  ASPECT_RATIOS,
  IMAGE_TIERS,
  VISIBLE_IMAGE_TIERS,
  type ImageAspectRatio,
  type ImageQualityTier,
} from "@/lib/imageProviders";
import { STYLE_PRESETS, getStylePreset, type StylePresetId } from "@/lib/stylePresets";
import { checkPromptBlocklist } from "@/lib/promptBlocklist";
import { track } from "@/lib/analytics";
import { RecreateFromHistoryModal } from "@/components/RecreateFromHistoryModal";

const imageSearchSchema = z.object({
  from: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/_authenticated/create/image")({
  head: () => ({
    meta: [
      { title: "AI画像生成 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: zodValidator(imageSearchSchema),
  component: ImageCreatePage,
});

const PROMPT_MAX = 500;

function ImageCreatePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [userId, setUserId] = useState<string | null>(null);
  const balance = useCredits(userId);

  const [prompt, setPrompt] = useState("");
  const [tier, setTier] = useState<ImageQualityTier>("basic");
  const [aspect, setAspect] = useState<ImageAspectRatio>("1:1");
  const [styleId, setStyleId] = useState<StylePresetId | null>(null);

  const [generating, setGenerating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [prefilling, setPrefilling] = useState(false);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  const applyFromGeneration = async (id: string) => {
    setPrefilling(true);
    try {
      const { data, error } = await supabase
        .from("generations")
        .select("prompt_original, style_preset, aspect_ratio, quality_tier")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) {
        toast.error("過去の生成を読み込めませんでした");
        return;
      }
      if (data.prompt_original) setPrompt(data.prompt_original.slice(0, PROMPT_MAX));
      if (data.style_preset) setStyleId(data.style_preset as StylePresetId);
      if (data.aspect_ratio) setAspect(data.aspect_ratio as ImageAspectRatio);
      if (data.quality_tier && data.quality_tier in IMAGE_TIERS) {
        setTier(data.quality_tier as ImageQualityTier);
      }
      toast.success("過去の設定を読み込みました");
    } finally {
      setPrefilling(false);
    }
  };

  // ?from={id} で来たら自動 pre-fill して URL から削除
  useEffect(() => {
    if (!search.from) return;
    const id = search.from;
    void (async () => {
      await applyFromGeneration(id);
      navigate({ to: "/create/image", search: {}, replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.from]);

  const tierConfig = IMAGE_TIERS[tier];
  const styleSuffix = useMemo(() => getStylePreset(styleId)?.promptSuffix ?? null, [styleId]);

  const blockedHit = useMemo(() => checkPromptBlocklist(prompt), [prompt]);
  const balanceLoaded = balance !== null;
  const canAfford = balanceLoaded && balance! >= tierConfig.credits;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("プロンプトを入力してください");
      return;
    }
    if (blockedHit) {
      toast.error(`使用できない単語が含まれています: ${blockedHit}`);
      return;
    }
    if (!canAfford) {
      toast.error("クレジットが足りません");
      return;
    }
    setGenerating(true);
    track("image_generation_start", {
      quality_tier: tier,
      aspect_ratio: aspect,
      style_preset: styleId,
    });
    try {
      const { data, error } = await supabase.functions.invoke("create-image", {
        body: {
          quality_tier: tier,
          prompt_original: prompt.trim(),
          style_preset: styleId,
          aspect_ratio: aspect,
        },
      });
      if (error) throw error;
      const genId = (data as { generation_id?: string } | null)?.generation_id;
      if (!genId) throw new Error("生成IDが取得できませんでした");
      track("image_generation_complete", {
        quality_tier: tier,
        success: true,
      });
      navigate({ to: "/create/image/result/$id", params: { id: genId } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "画像生成に失敗しました";
      track("image_generation_failed", { quality_tier: tier, error: msg });
      toast.error(msg);
      setGenerating(false);
    }
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8 pb-32 sm:py-10">
      {prefilling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm">
          <div className="mx-4 flex max-w-sm flex-col items-center gap-4 rounded-2xl border border-border bg-card px-8 py-10 text-center shadow-xl">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <Sparkles className="absolute inset-0 m-auto h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-base font-bold">設定を復元しています</p>
              <p className="text-xs text-muted-foreground">
                過去の生成からプロンプト・スタイルを取得中です。
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-3xl">
        <Link
          to="/create"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          新規作成に戻る
        </Link>
        <header className="mb-6 text-center">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Wand2 className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">AIで画像を作る</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            日本語でイメージを書くだけ。AIが英訳して画像を生成します。
          </p>
        </header>

        {/* 残高 */}
        <div className="mb-6 flex justify-center">
          <button
            type="button"
            onClick={() => navigate({ to: "/credits-shop" })}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium shadow-sm transition hover:border-primary hover:bg-primary/5"
          >
            <Gem className="h-4 w-4 text-primary" />
            残り
            <span className="text-base font-bold text-primary">{balance ?? "—"}</span>
            クレジット
          </button>
        </div>

        {/* 過去の生成から */}
        <div className="mb-4 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
          >
            <History className="mr-2 h-4 w-4" />
            過去の生成から
          </Button>
        </div>

        {/* プロンプト */}
        <Card className="p-5">
          <Label htmlFor="prompt" className="text-sm font-semibold">
            どんな画像にしたいですか？
          </Label>
          <Textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, PROMPT_MAX))}
            placeholder="例：夕焼けの海辺を歩く猫、シネマティックな構図"
            className="mt-2 min-h-[120px]"
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-xs text-destructive">
              {blockedHit ? `使用できない単語: ${blockedHit}` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {prompt.length} / {PROMPT_MAX}
            </p>
          </div>

        </Card>


        {/* スタイル */}
        <Card className="mt-4 p-5">
          <Label className="text-sm font-semibold">スタイル（任意）</Label>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <button
              type="button"
              onClick={() => setStyleId(null)}
              className={cn(
                "rounded-md border p-3 text-left text-xs transition",
                styleId === null
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50",
              )}
            >
              <div className="font-semibold">なし</div>
              <div className="mt-0.5 text-muted-foreground">写真風</div>
            </button>
            {STYLE_PRESETS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyleId(s.id)}
                className={cn(
                  "rounded-md border p-3 text-left text-xs transition",
                  styleId === s.id
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50",
                )}
              >
                <div className="font-semibold">{s.label}</div>
                <div className="mt-0.5 text-muted-foreground line-clamp-1">
                  {s.description}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* アスペクト比 */}
        <Card className="mt-4 p-5">
          <Label className="text-sm font-semibold">アスペクト比</Label>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {ASPECT_RATIOS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setAspect(a.id)}
                className={cn(
                  "rounded-md border p-3 text-sm font-medium transition",
                  aspect === a.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-primary/50",
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
        </Card>

        {/* 品質ティア */}
        <Card className="mt-4 p-5">
          <Label className="text-sm font-semibold">品質</Label>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {VISIBLE_IMAGE_TIERS.map((id) => {
              const c = IMAGE_TIERS[id];
              const selected = tier === id;
              const affordable = balanceLoaded && balance! >= c.credits;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTier(id)}
                  className={cn(
                    "rounded-md border p-3 text-left text-xs transition",
                    selected
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50",
                    !affordable && "opacity-60",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{c.label}</span>
                    <span className="inline-flex items-center gap-0.5 text-primary">
                      <Gem className="h-3 w-3" />
                      <span className="font-bold">{c.credits}</span>
                    </span>
                  </div>
                  <div className="mt-1 text-muted-foreground">{c.description}</div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Sticky Generate */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 px-4 py-4 shadow-lg backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-3 sm:flex-row">
          <div className="text-sm">
            <span className="text-muted-foreground">消費：</span>
            <span className="text-lg font-bold text-primary">
              <Gem className="mr-1 inline h-4 w-4" />
              {tierConfig.credits} クレジット
            </span>
            {balanceLoaded && (
              <span className="ml-2 text-xs text-muted-foreground">
                （残高 {balance} → {Math.max(0, balance! - tierConfig.credits)}）
              </span>
            )}
          </div>
          <Button
            size="lg"
            onClick={handleGenerate}
            disabled={generating || !prompt.trim() || !!blockedHit || !canAfford}
            className="h-12 px-8"
          >
            {generating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                生成中…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                画像を生成
                <ArrowRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      <RecreateFromHistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        kind="image"
        onPick={(id) => void applyFromGeneration(id)}
      />
    </main>
  );
}
